import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Avatar,
  alpha,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const lightColors = {
  background: "#ffffff",
  surface: "#f8f9fa",
  surfaceAlt: "#f1f3f5",
  border: "#e0e0e0",
  text: "#1a1a2e",
  textSecondary: "#5f6368",
  primary: "#1565c0",
  warning: "#e65100",
  warningBg: "rgba(230, 81, 0, 0.06)",
  warningBorder: "rgba(230, 81, 0, 0.2)",
  lockedBg: "rgba(211, 47, 47, 0.06)",
  lockedBorder: "rgba(211, 47, 47, 0.2)",
  lockedText: "#c62828",
  availableBg: "rgba(46, 125, 50, 0.06)",
  availableBorder: "rgba(46, 125, 50, 0.2)",
  availableText: "#2e7d32",
};

const darkColors = {
  background: "#1a1a2e",
  surface: "#16213e",
  surfaceAlt: "#0f3460",
  border: "#2a2a4a",
  text: "#e0e0e0",
  textSecondary: "#9e9e9e",
  primary: "#90caf9",
  warning: "#ffb74d",
  warningBg: "rgba(255, 183, 77, 0.08)",
  warningBorder: "rgba(255, 183, 77, 0.25)",
  lockedBg: "rgba(239, 154, 154, 0.08)",
  lockedBorder: "rgba(239, 154, 154, 0.25)",
  lockedText: "#ef9a9a",
  availableBg: "rgba(129, 199, 132, 0.08)",
  availableBorder: "rgba(129, 199, 132, 0.25)",
  availableText: "#81c784",
};

/**
 * RollingReservePopup
 *
 * Shown when /sendToOther responds with error_data === 3.
 * Displays the error message, locked rolling reserve amount, and withdrawable amount.
 *
 * Props:
 *   open                    {boolean}  - controls dialog visibility
 *   onClose                 {function} - called when dialog is dismissed
 *   errorMsg                {string}   - error.error_msg from the response
 *   lockedRollingReserve    {number}   - lockedRollingReserveValue from the response
 *   withdrawableValue       {number}   - withdrawableValue from the response
 *   theme                   {string}   - "dark" | "light"
 */
const RollingReservePopup = ({
  open,
  onClose,
  errorMsg = "",
  lockedRollingReserve = 0,
  withdrawableValue = 0,
  theme = "light",
}) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const { btncolor, allColors } = useSelector((state) => state);
  const colors = theme === "dark" ? darkColors : lightColors;
  const isDark = theme === "dark";
  const currencySymbol = localStorage.getItem("homeCurrencySymbol") || "$";

  const fmt = (val) =>
    Number(val).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "20px",
          backgroundColor: colors.background,
          color: colors.text,
          boxShadow: isDark
            ? "0 24px 64px rgba(0,0,0,0.6)"
            : "0 24px 64px rgba(0,0,0,0.12)",
          overflow: "hidden",
        },
      }}
    >
      {/* ── Header band ─────────────────────────────────────────── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${btncolor} 0%, ${alpha(btncolor, 0.8)} 100%)`,
          px: 3,
          pt: 3,
          pb: 3,
          position: "relative",
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.7)",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.12)",
              color: "#fff",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Icon + title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <LockIcon sx={{ color: "#fff", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}
            >
              Withdrawal Hold Notice
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.65)" }}
            >
              No funds are currently available for withdrawal
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Content ─────────────────────────────────────────────── */}
      <DialogContent sx={{ px: 3, pt: 3, pb: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

          {/* Error message */}
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              p: 2,
              borderRadius: "12px",
              backgroundColor: colors.warningBg,
              border: `1px solid ${colors.warningBorder}`,
            }}
          >
            <WarningAmberIcon
              sx={{
                color: colors.warning,
                fontSize: 20,
                flexShrink: 0,
                mt: "1px",
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: isDark ? colors.warning : colors.warning,
                lineHeight: 1.6,
              }}
            >
              {errorMsg ||
                "A portion of your balance is currently held as a rolling reserve and is not yet available for withdrawal."}
            </Typography>
          </Box>

          {/* Balance cards */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>

            {/* Locked Rolling Reserve */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                borderRadius: "12px",
                backgroundColor: colors.lockedBg,
                border: `1px solid ${colors.lockedBorder}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    backgroundColor: colors.lockedBorder,
                  }}
                >
                  <LockIcon
                    sx={{ fontSize: 15, color: colors.lockedText }}
                  />
                </Avatar>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textSecondary,
                      display: "block",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      fontSize: "0.65rem",
                    }}
                  >
                    Funds on Hold
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.lockedText,
                      fontWeight: 700,
                      fontSize: "1rem",
                      lineHeight: 1.2,
                    }}
                  >
                    {currencySymbol}{fmt(lockedRollingReserve)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Withdrawable Value */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                borderRadius: "12px",
                backgroundColor: colors.availableBg,
                border: `1px solid ${colors.availableBorder}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    backgroundColor: colors.availableBorder,
                  }}
                >
                  <LockOpenIcon
                    sx={{ fontSize: 15, color: colors.availableText }}
                  />
                </Avatar>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textSecondary,
                      display: "block",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      fontSize: "0.65rem",
                    }}
                  >
                    Available to Withdraw
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.availableText,
                      fontWeight: 700,
                      fontSize: "1rem",
                      lineHeight: 1.2,
                    }}
                  >
                    {currencySymbol}{fmt(withdrawableValue)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* ── Actions ─────────────────────────────────────────────── */}
      <Box sx={{ px: 3, pt: 2.5, pb: 3 }}>
        <Divider sx={{ borderColor: colors.border, mb: 2.5 }} />
        <Button
          onClick={onClose}
          variant="contained"
          fullWidth
          sx={{
            borderRadius: "12px",
            py: 1.5,
            fontWeight: 700,
            fontSize: "0.95rem",
            textTransform: "none",
            backgroundColor: btncolor,
            color: isDark ? allColors?.fontColor : allColors?.lightFontColor,
            boxShadow: `0 4px 16px ${alpha(btncolor, 0.35)}`,
            "&:hover": { backgroundColor: btncolor },
          }}
        >
          Understood
        </Button>
      </Box>
    </Dialog>
  );
};

export default RollingReservePopup;
